import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '../../services/auth';
import CampoSenha from '../../components/CampoSenha';
import RequisitosDeSenha from '../../components/RequisitosDeSenha';
import { senhaAtendeAPolitica } from '../../utils/politicaDeSenha';

/**
 * Troca da senha provisoria, exigida no primeiro acesso.
 *
 * Enquanto nao trocar, o servidor recusa todas as demais rotas, entao esta tela
 * nao tem menu nem saida a nao ser sair da conta. Nao e um alerta que se possa
 * dispensar: a senha ainda e conhecida por quem criou a conta.
 */
const TrocarSenha: React.FC = () => {
  const navigate = useNavigate();
  const usuario = authService.getUser();

  const [senhaAtual, setSenhaAtual] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const confere = senha.length > 0 && senha === confirmacao;
  const podeEnviar = senhaAtual.length > 0 && senhaAtendeAPolitica(senha) && confere && !salvando;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    setSalvando(true);
    try {
      await authService.trocarSenha({
        senha_atual: senhaAtual,
        password: senha,
        password_confirmation: confirmacao,
      });
      toast.success('Senha alterada. Bem-vindo!');
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      const dados = err?.response?.data;
      const primeiro = dados?.errors ? (Object.values(dados.errors).flat()[0] as string) : null;
      setErro(primeiro || dados?.message || 'Nao foi possivel alterar a senha.');
    } finally {
      setSalvando(false);
    }
  };

  const sair = async () => {
    await authService.logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Crie sua senha</h1>
          <p className="text-gray-600 mt-2">
            {usuario?.name ? `Olá, ${usuario.name.split(' ')[0]}. ` : ''}
            A senha atual foi definida por quem criou o seu acesso. Escolha uma que só você conheça
            para continuar.
          </p>
        </div>

        <form onSubmit={enviar} className="bg-white rounded-xl border shadow-sm p-6 space-y-4">
          {erro && (
            <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {erro}
            </div>
          )}

          <div>
            <label htmlFor="senha-atual" className="block text-sm font-medium text-gray-700 mb-1">
              Senha atual
            </label>
            <CampoSenha
              id="senha-atual"
              name="senha_atual"
              required
              autoComplete="current-password"
              placeholder="A senha que você recebeu"
              value={senhaAtual}
              onChange={(e) => setSenhaAtual(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="senha-nova" className="block text-sm font-medium text-gray-700 mb-1">
              Nova senha
            </label>
            <CampoSenha
              id="senha-nova"
              name="password"
              required
              autoComplete="new-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
            />
            <RequisitosDeSenha senha={senha} />
          </div>

          <div>
            <label htmlFor="senha-confirmacao" className="block text-sm font-medium text-gray-700 mb-1">
              Repita a nova senha
            </label>
            <CampoSenha
              id="senha-confirmacao"
              name="password_confirmation"
              required
              autoComplete="new-password"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
            />
            {confirmacao.length > 0 && !confere && (
              <p role="alert" className="text-xs text-red-600 mt-1">
                A confirmação não confere com a senha digitada.
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={!podeEnviar}
            className="w-full py-3 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {salvando ? 'Salvando...' : 'Salvar e entrar'}
          </button>
        </form>

        <button
          onClick={sair}
          className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-800"
        >
          <LogOut className="h-4 w-4" /> Sair e entrar com outra conta
        </button>
      </div>
    </div>
  );
};

export default TrocarSenha;
